import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const C = {
  bg: '#070810',
  bg2: '#0D0F1C',
  card: '#111425',
  green: '#00E87A',
  greenDim: 'rgba(0,232,122,0.12)',
  purple: '#9B6FD9',
  purple2: '#C49BFF',
  teal: '#00D4C4',
  white: '#F0F2FF',
  white2: '#B8BDD6',
  muted: '#525870',
  border: 'rgba(255,255,255,0.07)',
  border2: 'rgba(0,232,122,0.2)',
} as const;

const FONT = {
  display: "'Syne', sans-serif",
  body: "'Outfit', sans-serif",
  mono: "'JetBrains Mono', monospace",
} as const;

interface GlowDotProps {
  color?: string;
  size?: number;
  delay?: number;
}

function GlowDot({ color = C.green, size = 8, delay = 0 }: GlowDotProps) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setOn((v) => !v), 1200 + delay);
    return () => clearInterval(id);
  }, [delay]);
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        opacity: on ? 1 : 0.25,
        transition: 'opacity 0.8s ease',
        boxShadow: `0 0 ${size * 2}px ${color}`,
        flexShrink: 0,
      }}
    />
  );
}

type BadgeVariant = 'green' | 'purple' | 'white';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
}

function Badge({ children, variant = 'green' }: BadgeProps) {
  const styles: Record<BadgeVariant, { bg: string; color: string; border: string }> = {
    green: { bg: C.greenDim, color: C.green, border: 'rgba(0,232,122,0.25)' },
    purple: { bg: 'rgba(155,111,217,0.12)', color: C.purple2, border: 'rgba(155,111,217,0.25)' },
    white: { bg: 'rgba(240,242,255,0.05)', color: C.white2, border: C.border },
  };
  const s = styles[variant];
  return (
    <span
      style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        letterSpacing: '0.12em',
        padding: '5px 14px',
        borderRadius: 4,
        textTransform: 'uppercase',
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

interface StatCardProps {
  value: string;
  label: string;
  accent?: string;
}

function StatCard({ value, label, accent = C.green }: StatCardProps) {
  return (
    <div
      style={{
        background: C.bg2,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: '20px 22px',
        textAlign: 'center',
        minWidth: 140,
        flex: '1 1 140px',
      }}
    >
      <div style={{ fontFamily: FONT.display, fontSize: 28, fontWeight: 800, color: accent, lineHeight: 1 }}>
        {value}
      </div>
      <div
        style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          color: C.muted,
          marginTop: 6,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        {label}
      </div>
    </div>
  );
}

interface OttoHeadProps {
  size?: number;
}

function OttoHead({ size = 200 }: OttoHeadProps) {
  return (
    <svg width={size} height={size * 1.35} viewBox="0 0 200 270" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="100" cy="220" rx="60" ry="10" fill={C.green} fillOpacity="0.06" />
      <rect x="68" y="192" width="64" height="16" rx="4" fill="#1A1E35" stroke={C.green} strokeWidth="0.75" strokeOpacity="0.4" />
      <rect x="76" y="196" width="10" height="6" rx="2" fill={C.green} fillOpacity="0.35" />
      <rect x="90" y="196" width="20" height="6" rx="2" fill={C.purple} fillOpacity="0.25" />
      <rect x="114" y="196" width="10" height="6" rx="2" fill={C.green} fillOpacity="0.35" />
      <path d="M38 90 Q36 50 100 44 Q164 50 162 90 L162 156 Q162 185 100 188 Q38 185 38 156Z" fill="#111428" stroke="#1E2440" strokeWidth="1.5" />
      <path d="M38 98 Q34 88 38 80 L46 76 L46 170 L38 156Z" fill="#0D0F1E" stroke="#1A1D30" strokeWidth="0.75" />
      <path d="M162 98 Q166 88 162 80 L154 76 L154 170 L162 156Z" fill="#0D0F1E" stroke="#1A1D30" strokeWidth="0.75" />
      <rect x="34" y="104" width="12" height="24" rx="3" fill="#0A0C18" stroke={C.green} strokeWidth="0.5" strokeOpacity="0.4" />
      <rect x="37" y="108" width="6" height="2.5" rx="1" fill={C.green} fillOpacity="0.5" />
      <rect x="37" y="113" width="6" height="2.5" rx="1" fill={C.purple} fillOpacity="0.5" />
      <rect x="37" y="118" width="6" height="2.5" rx="1" fill={C.green} fillOpacity="0.3" />
      <rect x="154" y="104" width="12" height="24" rx="3" fill="#0A0C18" stroke={C.green} strokeWidth="0.5" strokeOpacity="0.4" />
      <rect x="157" y="108" width="6" height="2.5" rx="1" fill={C.green} fillOpacity="0.5" />
      <rect x="157" y="113" width="6" height="2.5" rx="1" fill={C.purple} fillOpacity="0.5" />
      <rect x="157" y="118" width="6" height="2.5" rx="1" fill={C.green} fillOpacity="0.3" />
      <line x1="100" y1="44" x2="100" y2="22" stroke="#2A2F4A" strokeWidth="1.5" />
      <circle cx="100" cy="19" r="4.5" fill={C.green} fillOpacity="0.9" />
      <circle cx="100" cy="19" r="7" fill={C.green} fillOpacity="0.12" />
      <path d="M56 90 Q56 72 100 68 Q144 72 144 90 L142 122 Q140 138 100 140 Q60 138 58 122Z" fill="#080C1A" stroke={C.green} strokeWidth="0.75" strokeOpacity="0.5" />
      <rect x="68" y="90" width="30" height="20" rx="6" fill="#050810" stroke={C.green} strokeWidth="0.75" strokeOpacity="0.7" />
      <rect x="71" y="94" width="24" height="12" rx="4" fill={C.green} fillOpacity="0.1" />
      <rect x="73" y="97" width="20" height="5" rx="2.5" fill={C.green} fillOpacity="0.8" />
      <circle cx="83" cy="99.5" r="2.5" fill={C.green} />
      <circle cx="83" cy="99.5" r="1.2" fill="#030804" />
      <rect x="102" y="90" width="30" height="20" rx="6" fill="#050810" stroke={C.green} strokeWidth="0.75" strokeOpacity="0.7" />
      <rect x="105" y="94" width="24" height="12" rx="4" fill={C.green} fillOpacity="0.1" />
      <rect x="107" y="97" width="20" height="5" rx="2.5" fill={C.green} fillOpacity="0.8" />
      <circle cx="117" cy="99.5" r="2.5" fill={C.green} />
      <circle cx="117" cy="99.5" r="1.2" fill="#030804" />
      <path d="M72 126 Q100 136 128 126" stroke={C.green} strokeWidth="0.75" strokeOpacity="0.4" fill="none" />
      <rect x="74" y="127" width="52" height="10" rx="3" fill="#060914" stroke="#1E2440" strokeWidth="0.5" />
      {[82, 89, 96, 103, 110, 117].map((x, i) => (
        <line key={x} x1={x} y1="130" x2={x} y2="135" stroke={C.green} strokeWidth="0.6" strokeOpacity={i === 3 ? 0.5 : 0.35} />
      ))}
      <circle cx="100" cy="116" r="2.5" fill="#0A0C1A" stroke={C.teal} strokeWidth="0.75" strokeOpacity="0.6" />
      <circle cx="100" cy="116" r="1" fill={C.teal} fillOpacity="0.7" />
    </svg>
  );
}

interface OttoMarkProps {
  size?: number;
}

function OttoMark({ size = 40 }: OttoMarkProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
      <path d="M18 4L30 10.5V25.5L18 32L6 25.5V10.5L18 4Z" stroke={C.green} strokeWidth="1.5" fill="none" />
      <path d="M18 9L25.5 13V21L18 25L10.5 21V13L18 9Z" fill={C.green} fillOpacity="0.15" stroke={C.green} strokeWidth="1" />
      <circle cx="18" cy="17" r="3" fill={C.green} />
      <circle cx="18" cy="17" r="1.2" fill="#030804" />
    </svg>
  );
}

interface StepCardProps {
  number: string;
  title: string;
  desc: string;
}

function StepCard({ number, title, desc }: StepCardProps) {
  return (
    <div
      style={{
        flex: '1 1 200px',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px 22px',
        position: 'relative',
      }}
    >
      <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.green, letterSpacing: '0.15em', marginBottom: 10 }}>
        {number}
      </div>
      <div style={{ fontFamily: FONT.display, fontSize: 17, fontWeight: 700, color: C.white, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontFamily: FONT.body, fontSize: 13, color: C.white2, lineHeight: 1.6 }}>{desc}</div>
    </div>
  );
}

export function Landing() {
  return (
    <div
      style={{
        background: C.bg,
        minHeight: '100vh',
        fontFamily: FONT.body,
        color: C.white,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundImage: `linear-gradient(rgba(0,232,122,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(0,232,122,0.015) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <nav
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 1100,
          margin: '0 auto',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <OttoMark size={32} />
          <span style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: 20, letterSpacing: -0.5 }}>
            Ott<span style={{ color: C.green }}>o</span>Bid
            <span style={{ color: C.purple2, fontWeight: 400 }}>.</span>
            <span style={{ color: C.purple2 }}>ai</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, fontSize: 13, color: C.white2 }}>
          <span style={{ cursor: 'pointer' }}>Product</span>
          <span style={{ cursor: 'pointer' }}>How it works</span>
          <span style={{ cursor: 'pointer' }}>About</span>
          <Link
            to="/login"
            style={{
              background: C.green,
              color: '#030804',
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 12,
              padding: '9px 20px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Sign in
          </Link>
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 40px', position: 'relative', zIndex: 1 }}>
        <div
          style={{
            height: 1,
            background: `linear-gradient(90deg, ${C.green}, ${C.teal}, ${C.purple}, ${C.purple2}, ${C.green})`,
            opacity: 0.4,
          }}
        />
      </div>

      <section
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1100,
          margin: '0 auto',
          padding: '80px 40px 60px',
          display: 'flex',
          alignItems: 'center',
          gap: 60,
          minHeight: 520,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 500,
            height: 500,
            background: `radial-gradient(ellipse at center, rgba(0,232,122,0.06) 0%, transparent 60%)`,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '30%',
            right: '15%',
            width: 300,
            height: 300,
            background: `radial-gradient(ellipse at center, rgba(155,111,217,0.04) 0%, transparent 60%)`,
            pointerEvents: 'none',
          }}
        />

        <div style={{ flex: 1, position: 'relative', zIndex: 2 }}>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 11,
              color: C.green,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: 18,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ display: 'inline-block', width: 24, height: 1, background: C.green }} />
            AI-Powered Rebar Estimating
          </div>

          <h1
            style={{
              fontFamily: FONT.display,
              fontSize: 48,
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: -1.5,
              color: C.white,
              marginBottom: 20,
            }}
          >
            The estimate engine
            <br />
            your crew <span style={{ color: C.green }}>actually</span>
            <br />
            wants to use.
          </h1>

          <p
            style={{
              fontFamily: FONT.body,
              fontSize: 15,
              color: C.white2,
              lineHeight: 1.7,
              maxWidth: 460,
              marginBottom: 32,
            }}
          >
            Upload your drawing set. Otto parses, extracts, and assembles your rebar takeoff — in minutes, not hours.
            Your estimators stay in control of every flag.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 36 }}>
            <Link
              to="/login"
              style={{
                background: C.green,
                color: '#030804',
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: 13,
                padding: '13px 28px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Submit a Drawing Set
            </Link>
            <a
              href="#how"
              style={{
                color: C.white2,
                fontSize: 13,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: FONT.body,
                cursor: 'pointer',
              }}
            >
              See how Otto works <span style={{ color: C.green }}>→</span>
            </a>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Badge variant="green">Human First</Badge>
            <Badge variant="purple">AI Powered</Badge>
            <Badge variant="white">Rebar-Native</Badge>
          </div>
        </div>

        <div
          style={{
            flex: '0 0 280px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <OttoHead size={220} />
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              color: C.green,
              letterSpacing: '0.15em',
              marginTop: -20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <GlowDot size={6} /> OTTO v1.0 — ONLINE
          </div>
        </div>
      </section>

      <section style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: '0 40px 60px' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <StatCard value="4.2 min" label="Avg Takeoff TAT" />
          <StatCard value="97%" label="Structural Accuracy" />
          <StatCard value="100+" label="Corpus Jobs" />
          <StatCard value="21" label="Field Schema" accent={C.teal} />
        </div>
      </section>

      <section
        id="how"
        style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: '40px 40px 80px' }}
      >
        <div style={{ height: 1, background: C.border, marginBottom: 60 }} />

        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 11,
            letterSpacing: '0.2em',
            color: C.green,
            textTransform: 'uppercase',
            marginBottom: 10,
            opacity: 0.85,
          }}
        >
          How It Works
        </div>
        <h2 style={{ fontFamily: FONT.display, fontSize: 32, fontWeight: 800, color: C.white, marginBottom: 8, lineHeight: 1.1 }}>
          Upload. Extract. Submit.
        </h2>
        <p style={{ fontFamily: FONT.body, fontSize: 14, color: C.white2, marginBottom: 40, maxWidth: 520 }}>
          Otto handles drawing parsing, material extraction, and weight assembly — so your estimators focus on what matters: closing jobs.
        </p>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <StepCard
            number="01"
            title="Upload Drawings"
            desc="Drop your PDF drawing set into the portal. Otto routes each sheet by discipline automatically."
          />
          <StepCard
            number="02"
            title="AI Extraction"
            desc="Specialized Ottobots parse rebar schedules, keynotes, and structural details across every sheet."
          />
          <StepCard
            number="03"
            title="Review & Submit"
            desc="Get a 21-field structured output with flagged items. Your estimators approve, adjust, and send to ASA."
          />
        </div>
      </section>

      <section style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: '0 40px 80px' }}>
        <div
          style={{
            background: `linear-gradient(135deg, rgba(0,232,122,0.05), ${C.card})`,
            border: `1px solid ${C.border2}`,
            borderRadius: 16,
            padding: '48px 48px',
            display: 'flex',
            alignItems: 'center',
            gap: 40,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: FONT.display,
                fontSize: 26,
                fontWeight: 800,
                lineHeight: 1.2,
                color: C.white,
                marginBottom: 12,
              }}
            >
              The AI that works <span style={{ color: C.purple2 }}>for your people.</span>
              <br />
              Not instead of them.
            </div>
            <p style={{ fontFamily: FONT.body, fontSize: 14, color: C.white2, lineHeight: 1.7, maxWidth: 440 }}>
              OttoBid is built on a simple principle: AI should handle volume work so skilled estimators can focus on
              judgment, relationships, and winning jobs. Otto is upfront about being AI — and proud of what he can do
              for your team.
            </p>
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div
              style={{
                fontFamily: FONT.display,
                fontSize: 22,
                fontWeight: 800,
                background: `linear-gradient(90deg, ${C.green}, ${C.teal}, ${C.purple2})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Ottobots, roll out.
            </div>
            <p style={{ fontFamily: FONT.mono, fontSize: 10, color: C.muted, textAlign: 'right' }}>
              6 specialized agents. 1 mission.
              <br />
              Your next bid, done faster.
            </p>
          </div>
        </div>
      </section>

      <footer style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: '0 40px 48px' }}>
        <div style={{ height: 1, background: C.border, marginBottom: 32 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: 18 }}>
              Otto<span style={{ color: C.green }}>Bid</span>.ai
            </div>
            <div style={{ fontFamily: FONT.mono, fontSize: 10, color: C.muted, marginTop: 4 }}>
              Optimizing Take-offs. Transforming Outcomes.
            </div>
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.muted, textAlign: 'right' }}>
            <span style={{ color: C.green }}>ottobid.ai</span>
            <br />
            <span style={{ opacity: 0.6 }}>© 2026 OttoBid Technologies</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
